import { Box, Typography } from "@mui/material";
import React, { useCallback } from "react";

import {
  CapabilityPartDetail,
  ClaimedRole,
  PartsClaimedWith,
} from "../../types/capabilityPartDetail";
import { EntityRef } from "../../types/productPlane";
import { entityLinkText } from "../Detail/EntityLink";
import { detailLabelStyle } from "../Detail/LabelValueGrid";
import { codeWords } from "../Detail/format";
import PartCountedList, { CountLink } from "./PartCountedList";
import { exploreAddress, exploreFilterOf } from "./partExplore";

interface PartClaimedWithProps {
  part: CapabilityPartDetail;
}

// The name of a role: of its list, and of one part of it
const ROLE_NAMES: Record<ClaimedRole, { list: string; one: string }> = {
  ability: { list: "Abilities", one: "ability" },
  condition: { list: "Conditions", one: "condition" },
  mode: { list: "Modes", one: "mode" },
  object: { list: "Objects", one: "object" },
  means: { list: "Means", one: "means" },
};

// The roles whose lists have no bars: what a claim acts on and by what
// means is listed, not weighed
const WITHOUT_BARS: ReadonlySet<ClaimedRole> = new Set<ClaimedRole>([
  "object",
  "means",
]);

// The roles the part is claimed with any part of
export const claimedWithGroups = (
  part: CapabilityPartDetail,
): PartsClaimedWith[] =>
  part.claimed_with.filter((group) => group.parts.items.length > 0);

const RoleList: React.FC<{
  part: CapabilityPartDetail;
  group: PartsClaimedWith;
}> = ({ part, group }) => {
  const names = ROLE_NAMES[group.role];

  // The count of an entry is the total of Explore with both parts picked,
  // where Explore has a filter for both. An object and a means have none:
  // their counts lead nowhere.
  const countLink = useCallback(
    (entity: EntityRef): CountLink | null => {
      const address = exploreAddress([
        { id: part.id, role: part.role },
        { id: entity.id, role: group.role },
      ]);
      return address === null
        ? null
        : {
            address,
            label: `Products with ${entityLinkText(part.type, part.label)} and ${entityLinkText(entity.type, entity.name)} in Explore`,
          };
    },
    [part.id, part.role, part.type, part.label, group.role],
  );

  return (
    <Box role="group" aria-label={names.list}>
      <Typography variant="caption" sx={{ ...detailLabelStyle, mb: 0.5 }}>
        {names.list}
      </Typography>
      <PartCountedList
        items={group.parts.items}
        total={group.parts.total}
        countLink={countLink}
        bars={!WITHOUT_BARS.has(group.role)}
        none={{
          label: `no ${names.one} stated`,
          count: group.products_without,
          out_of: group.out_of,
        }}
      />
    </Box>
  );
};

/**
 * What a part is claimed with: per other role the parts that the claims
 * carrying this part carry too, counted by product, and as the greyed last
 * entry of each list the products that name no part of the role. The name
 * of an entry opens that part; its count opens Explore with both parts
 * picked, where Explore has a filter for both.
 *
 * A role no claim names a part of is left out.
 */
const PartClaimedWith: React.FC<PartClaimedWithProps> = ({ part }) => (
  <Box>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        columnGap: 4,
        rowGap: 2.5,
      }}
    >
      {claimedWithGroups(part).map((group) => (
        <RoleList key={group.role} part={part} group={group} />
      ))}
    </Box>
    {exploreFilterOf(part.role) !== null && (
      <Typography
        variant="caption"
        component="p"
        color="text.disabled"
        sx={{ mt: 1.5, mb: 0 }}
      >
        A qualifier opens Explore with this {codeWords(part.role)} and that
        qualifier in one claim.
      </Typography>
    )}
  </Box>
);

export default PartClaimedWith;
